/**
 * DOCUMENTATION:
 * Component: GCPRunServerless
 * Purpose: Deep dive into Cloud Run, App Engine, and Cloud Functions.
 * * * Note for Junior Developers:
 * - Serverless: You don't manage servers. No patching, no OS updates.
 * - Cloud Run: Best for request-based containers (Web APIs, Microservices).
 * - Cloud Functions: Best for event-driven logic (e.g., "When a file is uploaded, resize it").
 * - App Engine: The 'Legacy' PaaS. Use it if you have a monolithic web app and don't want containers.
 */

import React from "react";
import { Link } from "react-router-dom";
import {
    Box, Card, Typography, Grid, Avatar, Button, styled, useTheme, useMediaQuery, Stack,
    Alert, Paper, Chip
} from "@mui/material";

/* Icons */
import BoltIcon from '@mui/icons-material/Bolt';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import AutoModeIcon from '@mui/icons-material/AutoMode';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundColor: "#0a192d",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(25, 40, 65, 0.85)",
    borderRadius: "14px",
    padding: theme.spacing(4),
    marginBottom: "25px",
    border: "1px solid rgba(66, 133, 244, 0.2)",
    color: "white",
}));

const FeatureBox = styled(Box)(({ theme, bordercolor }) => ({
    padding: theme.spacing(2),
    backgroundColor: "rgba(255, 255, 255, 0.03)",
    border: `1px solid ${bordercolor || "rgba(255, 255, 255, 0.1)"}`,
    borderRadius: "10px",
    height: "100%",
}));

export default function GCPRunServerless() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    return (
        <PageWrapper>
            <Box sx={{ textAlign: "center", mb: 6 }}>
                <Typography variant={isMobile ? "h4" : "h2"} fontWeight={900} color="#4285F4">
                    CLOUD RUN & SERVERLESS
                </Typography>
                <Typography variant="subtitle1" sx={{ opacity: 0.6 }}>Abstracting the Infrastructure</Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 8 } }}>

                {/* 1. Cloud Run: The Heavy Hitter */}
                <Grid item xs={12}>
                    <SectionCard>
                        <Stack direction="row" spacing={2} alignItems="center" mb={3}>
                            <CloudQueueIcon sx={{ color: "#4285F4", fontSize: 35 }} />
                            <Typography variant="h5" fontWeight={800}>Cloud Run (Knative based)</Typography>
                        </Stack>
                        <Typography variant="body1" sx={{ mb: 3, opacity: 0.9, lineHeight: 1.8 }}>
                            Cloud Run allows you to run **stateless containers**. It scales automatically
                            from zero to thousands of instances. You only pay for the exact CPU and Memory
                            used while your code is processing a request.
                        </Typography>



                        <Grid container spacing={3} sx={{ mt: 2 }}>
                            <Grid item xs={12} md={4}>
                                <FeatureBox bordercolor="#4285F4">
                                    <Typography variant="subtitle2" color="#4285F4">Concurrency</Typography>
                                    <Typography variant="caption">Unlike Cloud Functions, one Cloud Run instance can handle up to 250 requests at once, making it highly cost-efficient.</Typography>
                                </FeatureBox>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <FeatureBox bordercolor="#34A853">
                                    <Typography variant="subtitle2" color="#34A853">Any Language</Typography>
                                    <Typography variant="caption">If it can be containerized (Docker), it can run on Cloud Run. Go, Python, Rust, COBOL—doesn't matter.</Typography>
                                </FeatureBox>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <FeatureBox bordercolor="#FBBC04">
                                    <Typography variant="subtitle2" color="#FBBC04">Traffic Splitting</Typography>
                                    <Typography variant="caption">Native support for Canary deployments. Send 5% of traffic to a new version to test it.</Typography>
                                </FeatureBox>
                            </Grid>
                        </Grid>
                    </SectionCard>
                </Grid>

                {/* 2. The Serverless Decision Tree */}
                <Grid item xs={12} md={7}>
                    <SectionCard>
                        <Stack direction="row" spacing={2} alignItems="center" mb={3}>
                            <AutoModeIcon sx={{ color: "#FBBC04" }} />
                            <Typography variant="h5" fontWeight={800}>Architect's Choice</Typography>
                        </Stack>



                        <Stack spacing={2} mt={2}>
                            <Paper sx={{ p: 2, bgcolor: "rgba(255,255,255,0.03)", color: "white", borderLeft: "4px solid #4285F4" }}>
                                <Typography variant="subtitle2">Cloud Functions</Typography>
                                <Typography variant="caption">Best for <strong>Events</strong>. Triggered by Pub/Sub, Cloud Storage changes, or Firestore updates.</Typography>
                            </Paper>
                            <Paper sx={{ p: 2, bgcolor: "rgba(255,255,255,0.03)", color: "white", borderLeft: "4px solid #34A853" }}>
                                <Typography variant="subtitle2">App Engine Standard</Typography>
                                <Typography variant="caption">Best for <strong>Rapid Prototyping</strong> and specific languages (Java, PHP, Python) with no container overhead.</Typography>
                            </Paper>
                            <Paper sx={{ p: 2, bgcolor: "rgba(255,255,255,0.03)", color: "white", borderLeft: "4px solid #EA4335" }}>
                                <Typography variant="subtitle2">App Engine Flexible</Typography>
                                <Typography variant="caption">Best for <strong>Legacy Web Apps</strong> that need custom libraries or background threads.</Typography>
                            </Paper>
                        </Stack>
                    </SectionCard>
                </Grid>

                {/* 3. Event-Driven Logic */}
                <Grid item xs={12} md={5}>
                    <SectionCard sx={{ height: '100%' }}>
                        <Stack direction="row" spacing={2} alignItems="center" mb={3}>
                            <BoltIcon sx={{ color: "#34A853" }} />
                            <Typography variant="h5" fontWeight={800}>EventFlow</Typography>
                        </Stack>



                        <Typography variant="body2" sx={{ opacity: 0.8, mt: 2, mb: 2 }}>
                            Serverless thrives in an **Event-Driven Architecture (EDA)**.
                        </Typography>

                        <Stack direction="row" flexWrap="wrap" gap={1}>
                            <Chip label="Pub/Sub Trigger" size="small" variant="outlined" sx={{ color: "#4285F4", borderColor: "#4285F4" }} />
                            <Chip label="GCS Trigger" size="small" variant="outlined" sx={{ color: "#34A853", borderColor: "#34A853" }} />
                            <Chip label="Firestore Trigger" size="small" variant="outlined" sx={{ color: "#FBBC04", borderColor: "#FBBC04" }} />
                            <Chip label="Scheduler" size="small" variant="outlined" sx={{ color: "#EA4335", borderColor: "#EA4335" }} />
                        </Stack>
                    </SectionCard>
                </Grid>

                {/* PCA Exam Alert */}
                <Grid item xs={12}>
                    <Alert severity="warning" sx={{ bgcolor: "rgba(234, 67, 53, 0.1)", color: "white", border: "1px solid #EA4335" }}>
                        <strong>PCA ARCHITECT SCENARIO:</strong> "Your team wants to deploy a Dockerized Python API that will only be used 2 hours a day. They need to minimize costs and administrative overhead."
                        <br/><br/>
                        <strong>Correct Strategy:</strong> Deploy to <strong>Cloud Run</strong>. It is container-native (Docker), manages the infrastructure for you, and will scale to zero when not in use, meaning you pay $0 for the other 22 hours.
                    </Alert>
                </Grid>

                {/* Navigation */}
                <Grid item xs={12} textAlign="center">
                    <Link to="/gcp-dashboard" style={{ textDecoration: "none" }}>
                        <Button variant="contained" sx={{ bgcolor: "#4285F4", color: "white", fontWeight: 800, px: 6, py: 1.5, borderRadius: "999px" }}>
                            BACK TO DASHBOARD
                        </Button>
                    </Link>
                </Grid>
            </Grid>
        </PageWrapper>
    );
}
